import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { sanitizeText, sanitizeEmail, sanitizePhone, checkRateLimit, clearRateLimit } from "@/lib/security";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { CalendarIcon, CreditCard, DollarSign, Building, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  // Step 1: Basic Information
  name: z.string()
    .trim()
    .min(2, "Name must be at least 2 characters.")
    .max(100, "Name must be less than 100 characters.")
    .regex(/^[a-zA-ZÀ-ÿ\s\-']+$/, "Name contains invalid characters."),
  email: z.string()
    .trim()
    .min(1, "Email is required.")
    .email("Please enter a valid email address.")
    .max(255, "Email must be less than 255 characters.")
    .toLowerCase()
    .refine((email) => {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      return emailRegex.test(email);
    }, "Please enter a valid email address.")
    .refine((email) => {
      // Check for common disposable and fake email domains
      const disposableDomains = ['tempmail', 'throwaway', '10minutemail', 'guerrillamail', 'mailinator', 'trashmail', 'fakeinbox', 'maildrop', 'yopmail', 'temp-mail', 'getnada', 'dispostable', 'mintemail', 'sharklasers'];
      const domain = email.split('@')[1]?.toLowerCase() || '';
      return !disposableDomains.some(d => domain.includes(d));
    }, "Please use a valid email address.")
    .refine((email) => {
      // Check for valid email structure and common TLDs
      const parts = email.split('@');
      if (parts.length !== 2) return false;
      const [local, domain] = parts;
      const validTlds = ['com', 'net', 'org', 'edu', 'gov', 'mil', 'co', 'us', 'uk', 'ca', 'au', 'de', 'fr', 'es', 'it', 'br', 'mx', 'ar', 'cl', 'pe', 'co', 'info', 'biz', 'io', 'tech'];
      const domainParts = domain.split('.');
      const tld = domainParts[domainParts.length - 1];
      return local.length > 0 && domain.includes('.') && validTlds.includes(tld);
    }, "Please enter a valid email address with a recognized domain extension."),
  address: z.string()
    .trim()
    .min(10, "Address must be at least 10 characters.")
    .max(200, "Address must be less than 200 characters."),
  phone: z.string()
    .trim()
    .min(1, "Phone number is required.")
    .regex(/^[\d\s\-\(\)\+]+$/, "Phone number contains invalid characters.")
    .refine((phone) => {
      // Remove all non-digit characters for validation
      const digitsOnly = phone.replace(/\D/g, '');
      return digitsOnly.length >= 10 && digitsOnly.length <= 15;
    }, "Please enter a valid phone number (10-15 digits).")
    .refine((phone) => {
      // Check for sequential or repeated numbers
      const digitsOnly = phone.replace(/\D/g, '');
      const hasSequential = /(?:0123456789|1234567890|9876543210|0987654321)/.test(digitsOnly);
      const hasAllSame = /^(\d)\1+$/.test(digitsOnly);
      return !hasSequential && !hasAllSame;
    }, "Please enter a valid phone number."),
  practiceArea: z.string().min(1, "Please select a practice area."),
  caseExplanation: z.string()
    .trim()
    .min(20, "Please provide at least 20 characters explaining your case.")
    .max(2000, "Case explanation must be less than 2000 characters.")
    .refine((msg) => {
      const sqlPattern = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|DECLARE)\b)|(-{2})|([;'])/gi;
      return !sqlPattern.test(msg);
    }, "Case explanation contains invalid content."),
  questions: z.string()
    .trim()
    .min(10, "Please provide at least 10 characters for your questions.")
    .max(2000, "Questions must be less than 2000 characters.")
    .refine((msg) => {
      const sqlPattern = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|DECLARE)\b)|(-{2})|([;'])/gi;
      return !sqlPattern.test(msg);
    }, "Questions contain invalid content."),
  preferredLanguage: z.string().min(1, "Please select preferred language."),
  // Step 2: Appointment
  appointmentType: z.string().min(1, "Please select appointment type."),
  preferredDate: z.date({
    required_error: "Please select a preferred date.",
    invalid_type_error: "Please select a valid date.",
  }).refine((date) => {
    // Ensure date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today;
  }, "Date cannot be in the past."),
  preferredTime: z.string()
    .min(1, "Please select a preferred time.")
    .refine((time) => {
      // Validate time format (HH:MM)
      return /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
    }, "Please select a valid time."),
  // Step 3: Payment
  paymentMethod: z.string().min(1, "Please select a payment method."),
  zelleConfirmation: z.string().optional(),
  zelleReceiptUploaded: z.boolean().optional(),
  consent: z.boolean().refine((val) => val === true, {
    message: "You must agree to the terms to submit",
  }),
}).refine((data) => {
  // If payment method is Zelle, zelleConfirmation is required
  if (data.paymentMethod === 'Zelle') {
    return data.zelleConfirmation && data.zelleConfirmation.trim().length > 0;
  }
  return true;
}, {
  message: "Zelle confirmation number is required",
  path: ["zelleConfirmation"],
}).refine((data) => {
  // If payment method is Zelle, receipt must be uploaded
  if (data.paymentMethod === 'Zelle') {
    return data.zelleReceiptUploaded === true;
  }
  return true;
}, {
  message: "Payment receipt is required for Zelle payments",
  path: ["zelleReceiptUploaded"],
});

type FormData = z.infer<typeof formSchema>;

const ConsultationForm = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const totalSteps = 3;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      practiceArea: "",
      caseExplanation: "",
      questions: "",
      preferredLanguage: "",
      appointmentType: "",
      preferredTime: "",
      paymentMethod: "",
      zelleConfirmation: "",
      zelleReceiptUploaded: false,
      consent: false,
    },
  });

  const handleNext = async () => {
    let fieldsToValidate: (keyof FormData)[] = [];
    
    if (currentStep === 1) {
      fieldsToValidate = ['name', 'email', 'phone', 'address', 'practiceArea', 'caseExplanation', 'questions', 'preferredLanguage'];
    } else if (currentStep === 2) {
      fieldsToValidate = ['appointmentType', 'preferredDate', 'preferredTime'];
    }

    const isValid = await form.trigger(fieldsToValidate);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.size <= 20 * 1024 * 1024) {
      setUploadedFile(file);
      form.setValue('zelleReceiptUploaded', true);
    } else {
      setUploadedFile(null);
      form.setValue('zelleReceiptUploaded', false);
    }
  };

  const checkAvailability = async (date: Date, time?: string) => {
    setCheckingAvailability(true);
    setAvailabilityMessage(null);

    try {
      const formattedDate = format(date, 'yyyy-MM-dd');
      
      const { data, error } = await supabase.functions.invoke('check-calendar-availability', {
        body: {
          date: formattedDate,
          time: time,
          timezone: 'America/Chicago',
        }
      });

      if (error) {
        console.error('Availability check error:', error);
        setAvailabilityMessage({
          type: 'error',
          text: language === 'es' 
            ? 'No se pudo verificar la disponibilidad. Por favor, continúe con la reserva.'
            : language === 'vi'
            ? 'Không thể kiểm tra tính khả dụng. Vui lòng tiếp tục đặt lịch.'
            : 'Could not verify availability. Please continue with booking.'
        });
        return;
      }

      if (data?.available) {
        setAvailabilityMessage({
          type: 'success',
          text: language === 'es' 
            ? '✓ Esta fecha está disponible'
            : language === 'vi'
            ? '✓ Ngày này còn trống'
            : '✓ This date is available'
        });
      } else {
        const conflictMsg = data?.conflicting_events?.length > 0
          ? (language === 'es' 
              ? ` (${data.conflicting_events.length} evento(s) programado(s))`
              : language === 'vi'
              ? ` (${data.conflicting_events.length} sự kiện đã đặt)`
              : ` (${data.conflicting_events.length} event(s) scheduled)`)
          : '';
        
        setAvailabilityMessage({
          type: 'error',
          text: (language === 'es' 
            ? '⚠ Esta fecha puede no estar disponible'
            : language === 'vi'
            ? '⚠ Ngày này có thể không khả dụng'
            : '⚠ This date may not be available') + conflictMsg
        });
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailabilityMessage({
        type: 'error',
        text: language === 'es' 
          ? 'Error al verificar disponibilidad'
          : language === 'vi'
          ? 'Lỗi khi kiểm tra tính khả dụng'
          : 'Error checking availability'
      });
    } finally {
      setCheckingAvailability(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    if (!checkRateLimit('consultation_form', 3, 300000)) {
      toast.error(t('form.error'), {
        description: "Please wait a few minutes before submitting again.",
      });
      return;
    }

    // Explicit Zelle validation
    if (data.paymentMethod === 'Zelle') {
      const hasConfirmation = data.zelleConfirmation && data.zelleConfirmation.trim().length > 0;
      const hasReceipt = !!uploadedFile;
      
      if (!hasConfirmation || !hasReceipt) {
        toast.error(
          language === 'es' ? '⚠️ PROCESO INCOMPLETO' : language === 'vi' ? '⚠️ QUÁ TRÌNH CHƯA HOÀN TẤT' : '⚠️ INCOMPLETE PROCESS',
          {
            description: language === 'es'
              ? 'Para continuar debe: 1) Completar el pago por Zelle, 2) Ingresar el número de confirmación, y 3) Subir el comprobante de pago.'
              : language === 'vi'
              ? 'Để tiếp tục bạn phải: 1) Hoàn tất thanh toán Zelle, 2) Nhập số xác nhận, và 3) Tải lên biên lai thanh toán.'
              : 'To continue you must: 1) Complete the Zelle payment, 2) Enter the confirmation number, and 3) Upload the payment receipt.',
          }
        );
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      const sanitizedData = {
        name: sanitizeText(data.name) as string,
        email: sanitizeEmail(data.email) as string,
        phone: sanitizePhone(data.phone) as string,
        address: sanitizeText(data.address) as string,
        practiceArea: sanitizeText(data.practiceArea) as string,
        caseExplanation: sanitizeText(data.caseExplanation) as string,
        questions: sanitizeText(data.questions) as string,
        preferredLanguage: sanitizeText(data.preferredLanguage) as string,
        appointmentType: sanitizeText(data.appointmentType) as string,
        preferredDate: format(data.preferredDate, 'yyyy-MM-dd'),
        preferredTime: sanitizeText(data.preferredTime) as string,
        paymentMethod: sanitizeText(data.paymentMethod) as string,
        zelleConfirmation: data.zelleConfirmation ? (sanitizeText(data.zelleConfirmation) as string) : null,
        consent: data.consent,
      };

      if (sanitizedData.paymentMethod === 'Stripe') {
        // Create Stripe payment session
        const { data: paymentData, error: paymentError } = await supabase.functions.invoke('create-consultation-payment', {
          body: {
            consultationData: sanitizedData,
            language: language,
          }
        });

        if (paymentError) throw paymentError;

        if (paymentData?.url) {
          window.open(paymentData.url, '_blank');
          toast.success(t('form.success'), {
            description: language === 'es' 
              ? 'Complete el pago en la ventana que se abrió.' 
              : 'Complete the payment in the opened window.',
          });
        }
      } else {
        // For Zelle payments - save to database and send emails
        const bookingId = `CONS-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Get user IP for audit
        let ipAddress = 'unknown';
        try {
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          ipAddress = ipData.ip;
        } catch (e) {
          console.error('Could not get IP address:', e);
        }
        
        // Combine date and time into appointment timestamp
        const appointmentDateTime = new Date(`${sanitizedData.preferredDate}T${sanitizedData.preferredTime}`);
        const appointmentEnd = new Date(appointmentDateTime.getTime() + 60 * 60 * 1000);
        
        // Save to bookings table
        const bookingData = {
          booking_id: bookingId,
          service: sanitizedData.practiceArea,
          full_name: sanitizedData.name,
          date_of_birth: '1900-01-01', // Placeholder since consultation form doesn't collect this
          email: sanitizedData.email,
          phone: sanitizedData.phone,
          address: sanitizedData.address,
          situation: `${sanitizedData.caseExplanation}\n\nQuestions: ${sanitizedData.questions}`,
          appointment_type: sanitizedData.appointmentType,
          appointment_start: appointmentDateTime.toISOString(),
          appointment_end: appointmentEnd.toISOString(),
          payment_method: 'Zelle',
          payment_status: 'pending',
          zelle_confirmation: sanitizedData.zelleConfirmation,
          consent_notifications: true,
          consent_non_refundable: sanitizedData.consent,
          consent_ip: ipAddress,
          language: sanitizedData.preferredLanguage || language,
        };

        const { data: booking, error: bookingError } = await supabase
          .from('bookings')
          .insert(bookingData)
          .select()
          .single();

        if (bookingError) {
          console.error('Error saving consultation:', bookingError);
          throw bookingError;
        }

        console.log('[Consultation] Booking saved:', bookingId);

        // Create Zoom meeting for Video appointments
        let zoomData = null;
        if (sanitizedData.appointmentType === 'Video') {
          try {
            console.log('[Consultation] Creating Zoom meeting...');
            const { data: zoomResponse, error: zoomError } = await supabase.functions.invoke('create-zoom-meeting', {
              body: {
                topic: `Legal Consultation - ${sanitizedData.practiceArea}`,
                start_time: appointmentDateTime.toISOString(),
                duration: 60,
                timezone: 'America/Chicago',
                agenda: `Legal consultation with ${sanitizedData.name} regarding ${sanitizedData.practiceArea}`,
              },
            });

            if (zoomError) {
              console.error('[Consultation] Error creating Zoom meeting:', zoomError);
            } else {
              zoomData = zoomResponse;
              console.log('[Consultation] Zoom meeting created:', zoomData?.meeting_id);
            }
          } catch (zoomErr) {
            console.error('[Consultation] Zoom error:', zoomErr);
          }
        }

        // Create Google Calendar event
        let calendarEventId = null;
        try {
          console.log('[Consultation] Creating Google Calendar event...');
          
          let eventDescription = `Service: ${sanitizedData.practiceArea}\nClient: ${sanitizedData.name}\nPhone: ${sanitizedData.phone}\nEmail: ${sanitizedData.email}\nType: ${sanitizedData.appointmentType}\nPayment: Zelle`;
          
          if (sanitizedData.appointmentType === 'In-person') {
            eventDescription += '\n\nLocation: 10925 Beechnut St A106, Houston, TX 77072';
          } else if (sanitizedData.appointmentType === 'Phone') {
            eventDescription += `\n\nPhone call scheduled. Call client at: ${sanitizedData.phone}`;
          }
          
          const { data: calendarResponse, error: calendarError } = await supabase.functions.invoke('create-calendar-event', {
            body: {
              summary: `${sanitizedData.appointmentType === 'Video' ? '🎥' : sanitizedData.appointmentType === 'Phone' ? '📞' : '🏢'} Legal Consultation - ${sanitizedData.name}`,
              description: eventDescription,
              start_time: appointmentDateTime.toISOString(),
              end_time: appointmentEnd.toISOString(),
              timezone: 'America/Chicago',
              attendee_email: sanitizedData.email,
              attendee_name: sanitizedData.name,
              zoom_join_url: zoomData?.join_url,
            },
          });

          if (calendarError) {
            console.error('[Consultation] Error creating calendar event:', calendarError);
          } else {
            calendarEventId = calendarResponse?.event_id;
            console.log('[Consultation] Calendar event created:', calendarEventId);
          }
        } catch (calErr) {
          console.error('[Consultation] Calendar error:', calErr);
        }

        // Update booking with Zoom/Calendar data
        const updateData: any = {};
        if (zoomData) {
          updateData.zoom_meeting_id = zoomData.meeting_id;
          updateData.zoom_join_url = zoomData.join_url;
          updateData.zoom_password = zoomData.password;
          updateData.zoom_start_url = zoomData.start_url;
        }
        if (calendarEventId) {
          updateData.calendar_event_id = calendarEventId;
        }

        if (Object.keys(updateData).length > 0) {
          await supabase
            .from('bookings')
            .update(updateData)
            .eq('booking_id', bookingId);
        }

        // Send confirmation email to client
        try {
          console.log('[Consultation] Sending confirmation email...');
          const { error: emailError } = await supabase.functions.invoke('send-booking-confirmation', {
            body: {
              bookingId: bookingId,
              email: sanitizedData.email,
              fullName: sanitizedData.name,
              service: sanitizedData.practiceArea,
              appointmentType: sanitizedData.appointmentType,
              appointmentStart: appointmentDateTime.toISOString(),
              paymentMethod: 'Zelle',
              language: sanitizedData.preferredLanguage || language,
              zoomJoinUrl: zoomData?.join_url,
              zoomPassword: zoomData?.password,
              calendarEventId: calendarEventId,
            },
          });
          
          if (emailError) {
            console.error('[Consultation] Error sending confirmation email:', emailError);
          } else {
            console.log('[Consultation] Confirmation email sent successfully');
          }
        } catch (emailErr) {
          console.error('[Consultation] Email error:', emailErr);
        }

        // Send team notification
        try {
          console.log('[Consultation] Sending team notification...');
          const { error: notificationError } = await supabase.functions.invoke('notify-team-booking', {
            body: {
              bookingId: bookingId,
              fullName: sanitizedData.name,
              email: sanitizedData.email,
              phone: sanitizedData.phone,
              service: sanitizedData.practiceArea,
              appointmentType: sanitizedData.appointmentType,
              appointmentStart: appointmentDateTime.toISOString(),
              paymentMethod: 'Zelle',
              paymentStatus: 'pending',
              situation: `${sanitizedData.caseExplanation}\n\nQuestions: ${sanitizedData.questions}`,
              language: sanitizedData.preferredLanguage || language,
              zoomJoinUrl: zoomData?.join_url,
              zoomStartUrl: zoomData?.start_url,
              zoomPassword: zoomData?.password,
              calendarEventId: calendarEventId,
            },
          });
          
          if (notificationError) {
            console.error('[Consultation] Error sending team notification:', notificationError);
          } else {
            console.log('[Consultation] Team notification sent successfully');
          }
        } catch (notifErr) {
          console.error('[Consultation] Team notification error:', notifErr);
        }

        toast.success(
          language === 'es' ? '¡Consulta registrada con éxito!' : language === 'vi' ? 'Đăng ký tư vấn thành công!' : 'Consultation request submitted successfully!',
          {
            description: language === 'es' 
              ? 'Recibirá un correo de confirmación en breve.' 
              : language === 'vi'
              ? 'Bạn sẽ nhận được email xác nhận trong thời gian ngắn.'
              : 'You will receive a confirmation email shortly.',
          }
        );
      }
      
      form.reset();
      setUploadedFile(null);
      setCurrentStep(1);
      clearRateLimit('consultation_form');
    } catch (error) {
      console.error('Consultation submission error:', error instanceof Error ? error.message : 'Unknown error');
      toast.error(t('form.error'), {
        description: t('form.error.desc'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const paymentMethod = form.watch('paymentMethod');

  return (
    <div className="bg-card rounded-lg shadow-lg p-6 md:p-8">
      <h3 className="text-2xl font-bold mb-6">{t('form.title')}</h3>
      
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div 
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center font-semibold",
                  currentStep >= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                )}
              >
                {step}
              </div>
              {step < 3 && (
                <div className={cn(
                  "h-1 w-12 sm:w-20 mx-2",
                  currentStep > step ? "bg-primary" : "bg-muted"
                )} />
              )}
            </div>
          ))}
        </div>
        <p className="text-sm text-center text-muted-foreground">
          {t('booking.step')} {currentStep} {t('booking.of')} {totalSteps}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.name')} *</FormLabel>
                    <FormControl>
                      <Input placeholder="John Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.email')} *</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="john@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.phone')} *</FormLabel>
                    <FormControl>
                      <Input type="tel" placeholder="(832) 123-4567" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === 'es' ? 'Dirección' : language === 'vi' ? 'Địa chỉ' : 'Address'} *
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="123 Main St, Houston, TX 77002" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="practiceArea"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.area')} *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('form.select.placeholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="immigration">{t('practices.immigration')}</SelectItem>
                        <SelectItem value="criminal-defense">{t('practices.criminal')}</SelectItem>
                        <SelectItem value="personal-injury">{t('practices.injury')}</SelectItem>
                        <SelectItem value="family-law">{t('practices.family')}</SelectItem>
                        <SelectItem value="tax">{t('practices.tax')}</SelectItem>
                        <SelectItem value="probate-estate">{t('practices.probate')}</SelectItem>
                        <SelectItem value="real-estate">{t('practices.realestate')}</SelectItem>
                        <SelectItem value="business-law">{t('practices.business')}</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="caseExplanation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.caseExplanation')} *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('form.caseExplanation.placeholder')}
                        className="min-h-[120px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="questions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.questions')} *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder={t('form.questions.placeholder')}
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preferredLanguage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('consult.language')} *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('consult.language.placeholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="english">English</SelectItem>
                        <SelectItem value="spanish">Español</SelectItem>
                        <SelectItem value="vietnamese">Tiếng Việt</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Step 2: Appointment Selection */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-2">{t('consult.appointment.title')}</h3>
                <p className="text-muted-foreground">{t('consult.appointment.desc')}</p>
              </div>

              <FormField
                control={form.control}
                name="appointmentType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('booking.appointmentType')} *</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="grid grid-cols-1 gap-3"
                      >
                        <div className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:bg-accent/50" onClick={() => field.onChange('in-person')}>
                          <RadioGroupItem value="in-person" id="in-person" />
                          <label htmlFor="in-person" className="cursor-pointer flex-1">
                            {t('booking.inPerson')}
                          </label>
                        </div>
                        <div className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:bg-accent/50" onClick={() => field.onChange('phone')}>
                          <RadioGroupItem value="phone" id="phone" />
                          <label htmlFor="phone" className="cursor-pointer flex-1">
                            {t('booking.phoneCall')}
                          </label>
                        </div>
                        <div className="flex items-center space-x-3 border rounded-lg p-4 cursor-pointer hover:bg-accent/50" onClick={() => field.onChange('video')}>
                          <RadioGroupItem value="video" id="video" />
                          <label htmlFor="video" className="cursor-pointer flex-1">
                            {t('booking.video')}
                          </label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="preferredDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>
                      {language === 'es' ? 'Fecha *' : language === 'vi' ? 'Ngày *' : 'Date *'}
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>{t('consult.selectDate')}</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            if (date) {
                              const time = form.watch('preferredTime');
                              checkAvailability(date, time);
                            } else {
                              setAvailabilityMessage(null);
                            }
                          }}
                          disabled={(date) => {
                            // Disable past dates
                            if (date < new Date(new Date().setHours(0, 0, 0, 0))) {
                              return true;
                            }
                            // Only allow Monday (1), Wednesday (3), Friday (5)
                            const day = date.getDay();
                            return day !== 1 && day !== 3 && day !== 5;
                          }}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                    <FormDescription>
                      {language === 'es' 
                        ? 'Los horarios se muestran en Hora Central (Houston, TX).'
                        : language === 'vi'
                        ? 'Thời gian hiển thị theo Giờ miền Trung (Houston, TX).'
                        : 'Times shown are in Central Time (Houston, TX).'}
                    </FormDescription>
                    {checkingAvailability && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {language === 'es' ? 'Verificando disponibilidad...' : 
                         language === 'vi' ? 'Đang kiểm tra tính khả dụng...' : 
                         'Checking availability...'}
                      </div>
                    )}
                    {availabilityMessage && (
                      <Alert className={availabilityMessage.type === 'success' ? 'border-green-500 bg-green-50 dark:bg-green-950' : 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950'}>
                        <AlertDescription className={availabilityMessage.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-yellow-800 dark:text-yellow-200'}>
                          {availabilityMessage.text}
                        </AlertDescription>
                      </Alert>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch('preferredDate') && (
                <FormField
                  control={form.control}
                  name="preferredTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {language === 'es' ? 'Hora *' : language === 'vi' ? 'Giờ *' : 'Time *'}
                      </FormLabel>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          const date = form.watch('preferredDate');
                          if (date) {
                            checkAvailability(date, value);
                          }
                        }} 
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={
                              language === 'es' ? 'Seleccione una hora' : 
                              language === 'vi' ? 'Chọn giờ' : 
                              'Select a time'
                            } />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="13:00">1:00 PM</SelectItem>
                          <SelectItem value="13:30">1:30 PM</SelectItem>
                          <SelectItem value="14:00">2:00 PM</SelectItem>
                          <SelectItem value="14:30">2:30 PM</SelectItem>
                          <SelectItem value="15:00">3:00 PM</SelectItem>
                          <SelectItem value="15:30">3:30 PM</SelectItem>
                          <SelectItem value="16:00">4:00 PM</SelectItem>
                          <SelectItem value="16:30">4:30 PM</SelectItem>
                          <SelectItem value="17:00">5:00 PM</SelectItem>
                          <SelectItem value="17:30">5:30 PM</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <Alert>
                <CalendarIcon className="h-4 w-4" />
                <AlertDescription>
                  {t('booking.calendarNote')}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Step 3: Payment */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold mb-2">
                  {language === 'es' ? 'Método de Pago' : language === 'vi' ? 'Phương thức thanh toán' : 'Payment Method'}
                </h3>
                <p className="text-muted-foreground">
                  {language === 'es' 
                    ? 'Tarifa de consulta: $150 USD (no reembolsable)'
                    : language === 'vi'
                    ? 'Phí tư vấn: $150 USD (không hoàn lại)'
                    : 'Consultation Fee: $150 USD (non-refundable)'}
                </p>
              </div>

              <Alert className="mb-4 border-red-600 bg-red-50 dark:bg-red-950">
                <DollarSign className="h-4 w-4 text-red-600" />
                <AlertDescription>
                  <p className="font-bold mb-1 text-red-600">
                    {language === 'es' ? '$ Política de Pagos' : language === 'vi' ? '$ Chính sách thanh toán' : '$ Payment Policy'}
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-300 font-semibold">
                    {language === 'es'
                      ? 'La tarifa de consulta de $150 no es reembolsable. Este pago asegura su cita.'
                      : language === 'vi'
                      ? 'Phí tư vấn $150 không được hoàn lại. Khoản thanh toán này đảm bảo cuộc hẹn của bạn.'
                      : 'The $150 consultation fee is non-refundable. This payment secures your appointment.'}
                  </p>
                </AlertDescription>
              </Alert>

              <Alert className="mb-6 border-primary/50 bg-primary/5">
                <AlertDescription className="text-sm">
                  {language === 'es'
                    ? 'Si contrata nuestros servicios durante la consulta, los $150 se acreditarán a sus honorarios legales.'
                    : language === 'vi'
                    ? 'Nếu bạn đặt dịch vụ của chúng tôi trong buổi tư vấn, $150 sẽ được ghi có vào phí pháp lý của bạn.'
                    : 'If you book our services during the consultation, $150 will be credited towards your legal fees.'}
                </AlertDescription>
              </Alert>

              <FormField
                control={form.control}
                name="paymentMethod"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {language === 'es' ? 'Seleccione Método de Pago' : language === 'vi' ? 'Chọn phương thức thanh toán' : 'Select Payment Method'} *
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="grid grid-cols-1 gap-4"
                      >
                        <Card 
                          className={`cursor-pointer transition-all ${
                            field.value === 'Stripe' ? 'border-primary bg-primary/5' : ''
                          }`}
                          onClick={() => field.onChange('Stripe')}
                        >
                          <CardHeader>
                            <div className="flex items-center gap-3">
                              <RadioGroupItem value="Stripe" id="stripe" />
                              <CreditCard className="h-5 w-5 text-primary" />
                              <CardTitle className="text-base">
                                {language === 'es' ? 'Tarjeta de Crédito/Débito' : language === 'vi' ? 'Thẻ tín dụng/ghi nợ' : 'Credit/Debit Card'}
                              </CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <CardDescription>
                              {language === 'es'
                                ? 'Pago seguro con tarjeta. Procesado inmediatamente.'
                                : language === 'vi'
                                ? 'Thanh toán thẻ an toàn. Xử lý ngay lập tức.'
                                : 'Secure card payment. Processed immediately.'}
                            </CardDescription>
                          </CardContent>
                        </Card>

                        <Card 
                          className={`cursor-pointer transition-all ${
                            field.value === 'Zelle' ? 'border-primary bg-primary/5' : ''
                          }`}
                          onClick={() => field.onChange('Zelle')}
                        >
                          <CardHeader>
                            <div className="flex items-center gap-3">
                              <RadioGroupItem value="Zelle" id="zelle" />
                              <Building className="h-5 w-5 text-primary" />
                              <CardTitle className="text-base">Zelle</CardTitle>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <CardDescription>
                              {language === 'es'
                                ? 'Transferencia bancaria instantánea.'
                                : language === 'vi'
                                ? 'Chuyển khoản ngân hàng tức thì.'
                                : 'Instant bank transfer.'}
                            </CardDescription>
                          </CardContent>
                        </Card>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {paymentMethod === 'Stripe' && (
                <Alert>
                  <CreditCard className="h-4 w-4" />
                  <AlertDescription>
                    <p className="text-sm">
                      {language === 'es'
                        ? 'Será redirigido a Stripe para completar su pago.'
                        : language === 'vi'
                        ? 'Bạn sẽ được chuyển đến Stripe để hoàn tất thanh toán.'
                        : 'You will be redirected to Stripe to complete your payment.'}
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {paymentMethod === 'Zelle' && (
                <div className="space-y-4">
                  {/* Warning alert for incomplete Zelle */}
                  {(!form.watch('zelleConfirmation') || !uploadedFile) && (
                    <Alert className="border-destructive bg-destructive/10">
                      <AlertDescription>
                        <p className="font-bold text-destructive text-lg mb-2">
                          {language === 'es' ? '⚠️ PROCESO INCOMPLETO' : language === 'vi' ? '⚠️ QUÁ TRÌNH CHƯA HOÀN TẤT' : '⚠️ INCOMPLETE PROCESS'}
                        </p>
                        <p className="text-destructive font-medium mb-2">
                          {language === 'es'
                            ? 'No podrá enviar el formulario hasta completar los siguientes pasos:'
                            : language === 'vi'
                            ? 'Bạn không thể gửi biểu mẫu cho đến khi hoàn thành các bước sau:'
                            : 'You cannot submit the form until you complete the following steps:'}
                        </p>
                        <ul className="list-disc list-inside text-destructive space-y-1 text-sm">
                          <li className={form.watch('zelleConfirmation') ? 'line-through opacity-60' : 'font-bold'}>
                            {language === 'es' ? 'Ingresar el número de confirmación de Zelle' : language === 'vi' ? 'Nhập số xác nhận Zelle' : 'Enter the Zelle confirmation number'}
                          </li>
                          <li className={uploadedFile ? 'line-through opacity-60' : 'font-bold'}>
                            {language === 'es' ? 'Subir el comprobante de pago' : language === 'vi' ? 'Tải lên biên lai thanh toán' : 'Upload the payment receipt'}
                          </li>
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Success message when complete */}
                  {form.watch('zelleConfirmation') && uploadedFile && (
                    <Alert className="border-green-500 bg-green-500/10">
                      <AlertDescription className="text-green-700 dark:text-green-300 font-medium">
                        {language === 'es' 
                          ? '✓ Proceso Zelle completado correctamente. Puede continuar.'
                          : language === 'vi'
                          ? '✓ Quá trình Zelle hoàn tất. Bạn có thể tiếp tục.'
                          : '✓ Zelle process completed successfully. You may proceed.'}
                      </AlertDescription>
                    </Alert>
                  )}

                  <Card className={(!form.watch('zelleConfirmation') || !uploadedFile) ? 'border-destructive border-2' : 'border-green-500'}>
                    <CardHeader>
                      <CardTitle className="text-lg">
                        {language === 'es' ? 'Instrucciones Zelle' : language === 'vi' ? 'Hướng dẫn Zelle' : 'Zelle Instructions'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="font-semibold text-sm">
                          {language === 'es' ? 'Enviar pago a:' : language === 'vi' ? 'Gửi thanh toán đến:' : 'Send payment to:'}
                        </p>
                        <p className="text-sm font-mono bg-muted px-2 py-1 rounded">info@danielalbertlaw.com</p>
                        <p className="text-sm font-semibold mt-2">
                          {language === 'es' ? 'Monto:' : language === 'vi' ? 'Số tiền:' : 'Amount:'} <span className="text-lg">$150.00 USD</span>
                        </p>
                      </div>
                      
                      <FormField
                        control={form.control}
                        name="zelleConfirmation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={!form.watch('zelleConfirmation') ? 'text-destructive font-bold' : ''}>
                              {language === 'es' ? 'Número de Confirmación de Zelle' : language === 'vi' ? 'Số xác nhận Zelle' : 'Zelle Confirmation Number'} *
                            </FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                placeholder="Z123456789" 
                                className={!form.watch('zelleConfirmation') ? 'border-destructive border-2' : ''}
                              />
                            </FormControl>
                            <FormDescription>
                              {language === 'es'
                                ? 'REQUERIDO: Ingrese el número de confirmación después de completar la transferencia'
                                : language === 'vi'
                                ? 'BẮT BUỘC: Nhập số xác nhận sau khi hoàn tất chuyển khoản'
                                : 'REQUIRED: Enter confirmation number after completing the transfer'}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="space-y-2">
                        <FormLabel className={!uploadedFile ? 'text-destructive font-bold' : ''}>
                          {language === 'es' ? 'Subir Comprobante de Pago' : language === 'vi' ? 'Tải lên biên lai thanh toán' : 'Upload Payment Receipt'} *
                        </FormLabel>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileUpload}
                          className={`cursor-pointer ${!uploadedFile ? 'border-destructive border-2' : ''}`}
                        />
                        {uploadedFile ? (
                          <p className="text-sm text-green-600 font-medium">
                            ✓ {uploadedFile.name}
                          </p>
                        ) : (
                          <p className="text-sm text-destructive font-medium">
                            {language === 'es'
                              ? '⚠️ El comprobante de pago es obligatorio'
                              : language === 'vi'
                              ? '⚠️ Biên lai thanh toán là bắt buộc'
                              : '⚠️ Payment receipt is required'}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <FormField
                control={form.control}
                name="consent"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>
                        {t('form.consent')} *
                      </FormLabel>
                      <FormDescription className="text-xs">
                        {t('form.consent.desc')}
                      </FormDescription>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6">
            {currentStep > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                {t('booking.previous')}
              </Button>
            )}
            
            {currentStep < totalSteps ? (
              <Button
                type="button"
                variant="cta"
                onClick={handleNext}
                className="ml-auto"
              >
                {t('booking.next')}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="submit"
                variant="cta"
                className="ml-auto"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('form.submitting') : t('form.submit')}
              </Button>
            )}
          </div>
        </form>
      </Form>

      <p className="text-xs text-muted-foreground mt-6 text-center">
        <strong>*</strong> {t('form.note')}
      </p>
    </div>
  );
};

export default ConsultationForm;
